"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Services = void 0;
var _analytics = require("./analytics");
var _host = require("./host");
var _locale = _interopRequireDefault(require("./locale"));
var _http = require("./http");
var _tenants = require("./tenants");
var _parserMap = require("../utils/parsers/parserMap");
var _WindowAPIHelper = require("./WindowAPIHelper");
var _logger = require("./logger");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
// -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// Type definitions
// -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

// -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
// Services class
// -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
class Services {
  // -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  // Service initialization methods
  // -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  static initializeAnalyticsService(config) {
    if (!Services.analyticsInstance) {
      var _TENANT_SERVICE$confi;
      const analyticsServiceClass = ((_TENANT_SERVICE$confi = _tenants.TENANT_SERVICE[config.tenant]) === null || _TENANT_SERVICE$confi === void 0 ? void 0 : _TENANT_SERVICE$confi.analytics) || _analytics.AnalyticsService;
      Services.analyticsInstance = new analyticsServiceClass(config);
    }
  }
  static initializeHostService(params) {
    if (!Services.hostInstance) {
      var _TENANT_SERVICE$param;
      const hostServiceClass = ((_TENANT_SERVICE$param = _tenants.TENANT_SERVICE[params.config.tenant]) === null || _TENANT_SERVICE$param === void 0 ? void 0 : _TENANT_SERVICE$param.host) || _host.HostService;
      Services.hostInstance = new hostServiceClass(params);
    }
  }
  static initializeLocaleService(params) {
    if (!Services.localeInstance) {
      Services.localeInstance = new _locale.default({
        i18n: params.i18n,
        options: {
          ...params.localOptions,
          market: params.market
        }
      });
    }
  }
  static initializeHttpService(config) {
    if (!Services.httpInstance) {
      Services.httpInstance = new _http.HttpService({
        api: config.api,
        tenant: config.tenant,
        appId: config.appId
      });
    }
  }
  static initializeParsers(tenant) {
    if (!Services.ParsersInstance) {
      Services.ParsersInstance = (0, _parserMap.Parsers)(tenant);
    }
  }
  static initializeWindowService(_ref) {
    let {
      config,
      setMetadata,
      setFlags,
      setSkippedQuestionIds,
      openGuidedSellingModal,
      resetQuestionnaire,
      setAdditionalSummaries
    } = _ref;
    if (!Services.windowAPIInstance) {
      const windowAPIConfig = config.windowAPI;
      Services.windowAPIInstance = (0, _WindowAPIHelper.getWindowServiceInstance)({
        ...windowAPIConfig,
        setMetadata,
        setFlags,
        setSkippedQuestionIds,
        openGuidedSellingModal,
        resetQuestionnaire,
        setAdditionalSummaries,
        config
      });
    }
  }

  // -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  // The setupServices method is a static method that initializes the services
  // The method has been implemented to have default values for the parameters,
  // to make it easier to test the services
  // For normal usage, the method should be called with the required parameters
  // The method initializes the following services:
  // - Analytics Service
  // - Host Service
  // - Locale Service
  // - Http Service
  // - Window Service
  // - Parsers
  // - Logger
  // -------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
  static setupServices() {
    let {
      config = {
        tenant: 'demo',
        api: {
          baseURL: ''
        },
        localOptions: {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
          removeCurrencySpace: true
        },
        windowAPI: {
          enabled: true,
          exposeMarketFlows: false,
          enableDynamicFlow: false
        },
        market: 'demo',
        appId: 'fallback_app',
        logging: {
          segments: [...Object.values(_logger.Logger.LOGGER_SEGMENTS)],
          levels: ['info', 'warn', 'error']
        }
      },
      i18n = (key, _) => key,
      marketingId = '',
      addToCartCallback = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Add to callback called with fallback function');
      },
      openGuidedSellingModal = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Open guided selling modal called with fallback function');
      },
      closeGuidedSellingModal = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Close guided selling modal called with fallback function');
      },
      setMetadata = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Set metadata called with fallback function');
      },
      setFlags = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Set flags called with fallback function');
      },
      setSkippedQuestionIds = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Set skipped question ids called with fallback function');
      },
      resetQuestionnaire = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Reset questionnaire called with fallback function');
      },
      setAdditionalSummaries = () => {
        _logger.Logger.warn({
          segments: [_logger.Logger.LOGGER_SEGMENTS.SERVICES]
        })('Set additional summaries called with fallback function');
      }
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const filledConfig = {
      ...Services.defaultConfig,
      ...config
    };
    const {
      tenant,
      localOptions,
      market
    } = filledConfig;

    //initialize services

    //Analytics Service
    Services.initializeAnalyticsService(filledConfig);

    //Host Service
    Services.initializeHostService({
      marketingId,
      addToCartCallback,
      config: filledConfig,
      openGuidedSellingModal,
      closeGuidedSellingModal
    });

    //Locale Service
    Services.initializeLocaleService({
      i18n,
      localOptions,
      market
    });

    //Http Service
    Services.initializeHttpService(filledConfig);

    //Parsers
    Services.initializeParsers(tenant);

    //WindowAPI
    Services.initializeWindowService({
      enabled: filledConfig.windowAPI.enabled,
      exposeMarketFlows: filledConfig.windowAPI.exposeMarketFlows,
      enableDynamicFlow: filledConfig.windowAPI.enableDynamicFlow,
      config: filledConfig,
      setMetadata,
      setFlags,
      setSkippedQuestionIds,
      openGuidedSellingModal,
      resetQuestionnaire,
      setAdditionalSummaries
    });
    const {
      logging
    } = filledConfig;
    const {
      segments,
      levels
    } = logging || {};
    _logger.Logger.setSegments(segments);
    _logger.Logger.setLevels(levels);
  }
  static get analytics() {
    if (!Services.analyticsInstance) {
      Services.setupServices();
    }
    return Services.analyticsInstance;
  }
  static get host() {
    if (!Services.hostInstance) {
      Services.setupServices();
    }
    return Services.hostInstance;
  }
  static get locale() {
    if (!Services.localeInstance) {
      Services.setupServices();
    }
    return Services.localeInstance;
  }
  static get http() {
    if (!Services.httpInstance) {
      Services.setupServices();
    }
    return Services.httpInstance;
  }
  static get Parsers() {
    if (!Services.ParsersInstance) {
      Services.setupServices();
    }
    return Services.ParsersInstance;
  }
  static clearInstances() {
    Services.analyticsInstance = undefined;
    Services.hostInstance = undefined;
    Services.localeInstance = undefined;
    Services.httpInstance = undefined;
    Services.ParsersInstance = undefined;
    Services.windowAPIInstance = undefined;
  }
}
exports.Services = Services;
Services.analyticsInstance = void 0;
Services.hostInstance = void 0;
Services.localeInstance = void 0;
Services.httpInstance = void 0;
Services.ParsersInstance = void 0;
Services.windowAPIInstance = void 0;
Services.defaultConfig = {
  tenant: 'demo',
  api: {
    baseURL: ''
  },
  localOptions: {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    removeCurrencySpace: true
  },
  windowAPI: {
    enabled: true,
    exposeMarketFlows: false,
    enableDynamicFlow: false
  },
  market: 'demo',
  appId: 'fallback_app',
  logging: {
    segments: [...Object.values(_logger.Logger.LOGGER_SEGMENTS)],
    levels: ['info', 'warn', 'error']
  }
};